import { loadJsPDF } from "./pdfEngines";
import { addPreviewWatermarkJsPdf } from "./previewWatermark";
import { generateBankTemplateA, generateBankTemplateB, generateBankTemplateC } from "./bankStatementTemplates";
import { fetchCustomLayout, isCustomTemplate, renderLayout, preloadLayoutAssets } from "./layoutEngine";
import { pdfToPageImages } from "./pdfPageImages";
import { loadPdfJs } from "./pdfEngines";

// Set up pdf.js worker using unpkg CDN with correct version

// Convert PDF to image using pdf.js
async function convertPdfToImage(pdfDataUrl) {
  // Convert base64 to ArrayBuffer
  const base64Data = pdfDataUrl.split(',')[1];
  const binaryString = atob(base64Data);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  
  const loadingTask = (await loadPdfJs()).getDocument({ data: bytes.buffer });
  const pdf = await loadingTask.promise;
  
  // Get the first page
  const page = await pdf.getPage(1);
  
  // Set scale for good quality preview
  const scale = 2;
  const viewport = page.getViewport({ scale });
  
  // Create a canvas to render the PDF page
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  
  // Render the page to canvas
  await page.render({
    canvasContext: context,
    viewport: viewport
  }).promise;
  
  // Convert canvas to image data URL
  const imageDataUrl = canvas.toDataURL('image/png', 0.9);
  
  return imageDataUrl;
}

function parseCurrency(s) {
  const cleaned = String(s || "").replace(/[^0-9.-]/g, "");
  if (cleaned === "" || cleaned === "." || cleaned === "-") return 0;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function formatShortDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  const month = d.getMonth() + 1;
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

function formatDateLong(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleString("en-US", {
    month: "long",
    day: "2-digit",
    year: "numeric",
  });
}


// Generate preview PDF as base64 data URL
// Builds the watermarked preview document (all pages) — shared by the
// single-image and all-pages exports below.
async function buildPreviewDoc(formData, template = 'template-a') {
  const {
      accountName,
      accountAddress1,
      accountAddress2,
      accountNumber,
      selectedMonth,
      beginningBalance,
      transactions,
      bankLogo
    } = formData;

    // Need minimum data to generate preview
    if (!selectedMonth) {
      return null;
    }

    const doc = new (await loadJsPDF())({ unit: "pt", format: "letter" });

    // Mirror the download generator: coerce every jsPDF.text argument so blank
    // optional fields (address lines, empty cells) can't throw and blank the
    // live preview.
    const _origText = doc.text.bind(doc);
    doc.text = (txt, ...rest) => _origText(
      txt == null ? "" : (Array.isArray(txt) ? txt.map((t) => (t == null ? "" : String(t))) : String(txt)),
      ...rest
    );

    const margin = 25;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Calculate statement dates
    const [year, month] = selectedMonth.split("-").map(Number);
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0);
    const statementStart = start.toISOString().split("T")[0];
    const statementEnd = end.toISOString().split("T")[0];

    // Calculate summary
    const beginning = parseCurrency(beginningBalance);
    let ending = beginning;
    let deposits = 0, purchases = 0, transfers = 0, refunds = 0, atmWithdrawals = 0, fees = 0;

    (transactions || []).forEach((tx) => {
      const amount = parseCurrency(tx.amount);
      switch (tx.type) {
        case "Deposit":
          deposits += amount;
          ending += amount;
          break;
        case "Refund":
          refunds += amount;
          ending += amount;
          break;
        case "Purchase":
          purchases += amount;
          ending -= amount;
          break;
        case "Transfer":
          transfers += amount;
          ending -= amount;
          break;
        case "Withdrawal":
        case "ATM":
          atmWithdrawals += amount;
          ending -= amount;
          break;
        case "Fee":
          fees += amount;
          ending -= amount;
          break;
        default:
          purchases += amount;
          ending -= amount;
          break;
      }
    });

    // Helper function for formatting numbers
    const toFixed = (n) =>
      n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    // Calculate month text and date range
    const monthText = new Date(year, month - 1).toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    });
    const dateRange = `(${formatDateLong(statementStart)} - ${formatDateLong(statementEnd)})`;

    // Prepare template data (matching what the main generator passes)
    // Pass the uploaded logo for preview - user can see their logo in real-time
    const templateData = {
      accountName: accountName || "Account Holder",
      accountAddress1: accountAddress1 || "123 Main Street",
      accountAddress2: accountAddress2 || "City, State 12345",
      accountNumber: accountNumber || "XXXX-XXXX",
      year,
      month,
      selectedMonth,
      statementStart,
      statementEnd,
      beginning,
      ending,
      deposits,
      purchases,
      transfers,
      refunds,
      atmWithdrawals,
      fees,
      monthText,
      dateRange,
      transactions: transactions || [],
      toFixed,
      formatShortDate,
      formatDateLong,
      parseCurrency,
      bankLogo: bankLogo || null  // Use uploaded logo in preview
    };

    // Generate the template based on selection (custom = a designed layout)
    if (isCustomTemplate(template)) {
      const customLayout = await fetchCustomLayout(template);
      if (customLayout) {
        await preloadLayoutAssets(customLayout, { formData }, 'bank-statement');
        renderLayout(doc, customLayout, { formData }, 'bank-statement');
      } else {
        await generateBankTemplateA(doc, templateData, pageWidth, pageHeight, margin);
      }
    } else {
      switch (template) {
        case 'template-b':
          generateBankTemplateB(doc, templateData, pageWidth, pageHeight, margin);
          break;
        case 'template-c':
          await generateBankTemplateC(doc, templateData, pageWidth, pageHeight, margin);
          break;
        case 'template-a':
        default:
          await generateBankTemplateA(doc, templateData, pageWidth, pageHeight, margin);
          break;
      }
    }

    // Add watermark on ALL pages
    await addPreviewWatermarkJsPdf(doc, pageWidth, pageHeight);

    return doc;
}

export const generateBankStatementPreview = async (formData, template = 'template-a') => {
  try {
    const doc = await buildPreviewDoc(formData, template);
    if (!doc) return null;
    // Convert first page to image for card/sample display
    const imageDataUrl = await convertPdfToImage(doc.output('dataurlstring'));
    return imageDataUrl;
  } catch (error) {
    console.error("Error generating bank statement preview:", error);
    return null;
  }
};

// All pages, for the in-app preview modal's page slider + swipe
export const generateBankStatementPreviewPages = async (formData, template = 'template-a') => {
  try {
    const doc = await buildPreviewDoc(formData, template);
    if (!doc) return null;
    return await pdfToPageImages(doc.output('arraybuffer'));
  } catch (error) {
    console.error("Error generating bank statement preview pages:", error);
    return null;
  }
};
